import React from 'react'
import { Flex, Tag } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { actions } from '../../../../../store/reducers/assignment.reducer'
import { PATTERN_TYPES } from '../../../../../constants/pattern-types'
import { useThemeColors } from '../../../../../hooks/useThemeColors'

const GroupsFilterTags = () => {
    const filters = useSelector(state => state.assignment.filters)
    const patterns = useSelector(state => state.assignment.assignment.patterns)
    const dispatch = useDispatch()
    const colors = useThemeColors()

    const handleClosePatternFilterTag = closedTagValue => {
        const _patterns = JSON.parse(JSON.stringify(filters.patterns))
        const _pattern = _patterns.find(pattern => pattern.id === closedTagValue)
        _pattern.enabled = false
        dispatch(actions.setPatternFilter(_patterns))
    }

    const handleCloseCriteriaFilterTag = (patternId, criteriaId) => {
        dispatch(actions.setPatternCriteriaFilter(patternId, criteriaId, false, true))
    }

    const handleCloseUnitTestFilterTag = closedTagValue => {
        let _unitTests = filters.unitTests.map(unitTest =>
            unitTest.id === closedTagValue ? { ...unitTest, enabled: false } : unitTest,
        )
        dispatch(actions.setUnitTestsFilter(_unitTests))
    }

    const handleResetFilters = () => {
        const resetPatterns = filters.patterns.map(pattern => {
            const resetCriteria = pattern.criteria ? 
                pattern.criteria.map(criterion => ({
                    ...criterion,
                    enabled: false,
                    show: true
                })) : [];
                
            return {
                ...pattern, 
                enabled: false, 
                show: true,
                criteria: resetCriteria
            };
        });
        
        dispatch(actions.setPatternFilter(resetPatterns))
        dispatch(actions.setUnitTestsFilter(filters.unitTests.map(unitTest => ({ ...unitTest, enabled: false }))))
        dispatch(actions.setMissingPatternFeedbackFilter(false))
        dispatch(actions.setMissingGroupFeedbackFilter(false))
    }

    const patternFilterTags = filters.patterns.reduce((result, pattern) => {
        if (pattern.enabled)
            result.push(
                <Tag key={'pattern-' + pattern.id} closable onClose={() => handleClosePatternFilterTag(pattern.id)}>
                    {pattern.show ? <EyeOutlined /> : <EyeInvisibleOutlined />} {pattern.name}
                </Tag>,
            )
        return result
    }, [])
    
    // Add tags for enabled criteria
    const criteriaFilterTags = filters.patterns.reduce((result, pattern) => {
        if (pattern.type === PATTERN_TYPES.SEMANTIC.value && pattern.criteria) {
            // Find the original pattern to get the full name
            const originalPattern = patterns?.find(p => p.motif_id === pattern.id);
            const patternName = originalPattern?.motif_caption || pattern.name;
            
            pattern.criteria.forEach((criterion, index) => {
                if (criterion.enabled) {
                    // Truncate criterion text for display
                    const criterionText = criterion.text.length > 30 
                        ? `${criterion.text.substring(0, 30)}...` 
                        : criterion.text;
                    
                    result.push(
                        <Tag 
                            key={`criterion-${pattern.id}-${index}`} 
                            closable 
                            onClose={() => handleCloseCriteriaFilterTag(pattern.id, index)}
                        >
                            {criterion.show ? <EyeOutlined /> : <EyeInvisibleOutlined />} {patternName}: {criterionText}
                        </Tag>
                    );
                }
            });
        }
        return result;
    }, []);

    const unitTestsFilterTags = filters.unitTests.reduce((arr, unitTest) => {
        if (unitTest.enabled) {
            arr.push(
                <Tag key={'ut-' + unitTest.id} closable onClose={() => handleCloseUnitTestFilterTag(unitTest.id)}>
                    {unitTest.passed ? (
                        <CheckCircleOutlined style={{ color: colors.success }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: colors.error }} />
                    )}{' '}
                    {unitTest.name}
                </Tag>,
            )
        }
        return arr
    }, [])

    const missingPatternFeedbackFilterTag = (
        <Tag
            key='missingPatternFeedbackFilterTag'
            closable
            onClose={() => dispatch(actions.setMissingPatternFeedbackFilter(false))}
        >
            Missing pattern feedback
        </Tag>
    )

    const missingGroupFeedbackFilterTag = (
        <Tag
            key='missingGroupFeedbackFilterTag'
            closable
            onClose={() => dispatch(actions.setMissingGroupFeedbackFilter(false))}
        >
            Missing group feedback
        </Tag>
    )

    const filterTags = [...patternFilterTags, ...criteriaFilterTags, ...unitTestsFilterTags]

    if (filters.feedback.missingGroupFeedback) filterTags.push(missingGroupFeedbackFilterTag)
    if (filters.feedback.missingPatternFeedback) filterTags.push(missingPatternFeedbackFilterTag)

    if (filterTags.length)
        filterTags.unshift(
            <Tag key='reset-filters' style={{ cursor: 'pointer' }} color={colors.error} onClick={handleResetFilters}>
                Reset Filters
            </Tag>,
        )

    return (
        <Flex wrap='wrap' gap={4}>
            {filterTags}
        </Flex>
    )
}

export default GroupsFilterTags
